import { Key } from 'react';

import { TreeData, TreeDataNode, TreeNodeState } from './tree.types';

export const getKey = (item: TreeData) => (item.id ?? item.title).toString();

export const matchesSearch = (item: TreeData, searchTerm: string) => {
  const search = searchTerm.toLowerCase();
  return (
    item.id?.toString().toLowerCase().includes(search) ||
    item.value?.toLowerCase().includes(search) ||
    item.title.toLowerCase().includes(search)
  );
};

export const treeMatchesSearch = (item: TreeData, searchTerm: string): boolean => {
  return (
    matchesSearch(item, searchTerm) ||
    (!!item.children && item.children.some((child) => treeMatchesSearch(child, searchTerm)))
  );
};

export const getDescendantKeys = (children: TreeDataNode[]): Set<Key> => {
  const keys = new Set<Key>();
  const stack = [...children];

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) break;

    keys.add(node.id);
    if (node.children) {
      stack.push(...node.children);
    }
  }

  return keys;
};

export const setDescendantChecked = (isChecked: boolean, state: TreeNodeState) => {
  const descendants = getDescendantKeys(state.children);
  if (isChecked) {
    for (const descendant of descendants) {
      state.checkedKeys.current.add(descendant);
    }
  } else {
    state.checkedKeys.current.delete(state.id);
    for (const key of descendants) {
      state.checkedKeys.current.delete(key);
    }
  }
};

export const setIsCheckedAction = (state: TreeNodeState, isChecked: boolean, isHalfChecked: boolean) => {
  if (isChecked) state.checkedKeys.current.add(state.id);
  else state.checkedKeys.current.delete(state.id);

  return { ...state, isChecked: isChecked, isHalfChecked: isHalfChecked };
};

export const getNumberOfChildrenChecked = (children: TreeDataNode[], checkedKeys: React.MutableRefObject<Set<Key>>) => {
  return children.reduce((pv, cv) => {
    return pv + (checkedKeys.current.has(cv.id) ? 1 : 0);
  }, 0);
};

export const hasDescendantsChecked = (
  checkedKeys: React.MutableRefObject<Set<Key>>,
  children?: TreeDataNode[]
): boolean => {
  if (!children) return false;
  return children.some(
    (node: TreeDataNode) => checkedKeys.current.has(node.id) || hasDescendantsChecked(checkedKeys, node.children)
  );
};

export const updateCheckedKeysAndGetCheckedState = (
  id: Key,
  childIsChecked: boolean,
  childKey: Key,
  children: TreeDataNode[],
  checkedKeys: React.MutableRefObject<Set<Key>>
) => {
  // Keep track of which nodes are checked for the sake of returning that
  // data in a callback
  if (childIsChecked) checkedKeys.current.add(childKey);
  else checkedKeys.current.delete(childKey);

  // This handles the checkbox state based on child checkboxes
  const numChecked = getNumberOfChildrenChecked(children, checkedKeys);
  const shouldBeChecked = numChecked === children.length;
  const shouldBeHalfChecked = !shouldBeChecked && hasDescendantsChecked(checkedKeys, children);

  if (shouldBeChecked) checkedKeys.current.add(id);
  else checkedKeys.current.delete(id);

  return { shouldBeChecked, shouldBeHalfChecked };
};

export const toggleIsExpanded = (state: TreeNodeState, isExpanded: boolean) => {
  if (isExpanded) state.expandedKeys.current.delete(state.id);
  else state.expandedKeys.current.add(state.id);
  state.onExpandCallback?.(state.expandedKeys.current);
  return { ...state, isExpanded: !isExpanded };
};

export const expandParentsOfItemsMatchingSearch = (
  id: Key,
  node: TreeData,
  expandedKeys: React.MutableRefObject<Set<Key>>,
  parentId?: Key,
  searchTerm?: string
) => {
  if (searchTerm) {
    expandedKeys.current.delete(id);
    if (matchesSearch(node, searchTerm) && parentId) {
      expandedKeys.current.add(parentId);
    }
  }
};

export const getTreeNodeDefaultsFromChildren = (
  id: Key,
  checkedKeys: React.MutableRefObject<Set<Key>>,
  expandedKeys: React.MutableRefObject<Set<Key>>,
  children?: TreeDataNode[]
) => {
  if (children?.every((node) => node.isChecked)) {
    checkedKeys.current.add(id);
  }
  if (children?.some((node) => node.isExpanded)) {
    expandedKeys.current.add(id);
  }
};

export const removeRedundantKeys = (keys: Set<Key>, data: TreeData[]) => {
  const newKeys = new Set<Key>();
  const nodes = [...data];

  while (nodes?.length) {
    const node = nodes.pop();
    if (!node) break;

    // Only add the node if it is selected
    if (keys.has(getKey(node))) {
      newKeys.add(getKey(node));
    } else if (node.children) {
      nodes.push(...node.children);
    }
  }

  return newKeys;
};
